import React, { useState } from "react";

import { TabContext, TabPanel } from "@mui/lab";
import { Box, Divider, Tab, Tabs } from "@mui/material";

import { Comments, Log } from "../../../shared/components";

import { RaiseCause } from "./RaiseCause";
import { RaiseAssessment } from "./RaiseAssessment";
import { RaiseMitigation } from "./RaiseMitigation";
import { RiskDetailsOverview } from "./RiskDetailsOverview";
import { useCause } from "../hooks/useCause";
import { useAssessment } from "../hooks/useAssessment";
import { useMitigation } from "../hooks/useMitigation";

export const RiskDetails: React.FC<{ riskId: string | null; onEdit: () => void; refetch: () => void }> = ({ riskId, onEdit, refetch }) => {

    const [currentTab, setCurrentTab] = useState<string>("overview");
    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    const {
        causesData, setCausesData,
        editCauseStatus, onSaveCause, onEditCause, onClearCause,
        causeData, isCauseFetching, isCausePending, isSaving: causeSaving
    } = useCause({ riskId, refetch: refetch });

    const {
        impactData, setImpactData,
        editImpactStatus, onSaveImpact, onEditImpact, onClearImpact,
        assessmentData, isAssessmentFetching, isAssessmentPending, isSaving: assessmentSaving
    } = useAssessment({ riskId, refetch: refetch });

    const {
        editMitigationStatus, onSaveMitigation, onEditMitigation, onClearMitigation,
        mitigationsData, setMitigationsData,
        mitigationData, isMitigationFetching, isMitigationPending, isSaving: mitigationSaving
    } = useMitigation({ riskId, refetch: refetch });

    const isAssessmentTabDisabled = !causeData || causeData.length === 0;
    const isMitigationTabDisabled = !assessmentData || assessmentData.length === 0;

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'}>
            <TabContext value={currentTab}>
                <Tabs
                    value={currentTab}
                    onChange={handleChangeTab}
                    aria-label="Tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ minHeight: "auto" }}
                >
                    {[
                        { value: "overview", label: "OVERVIEW" },
                        { value: "causes", label: "Causes" },
                        { value: "assessment", label: "Assessment", disabled: isAssessmentTabDisabled },
                        { value: "mitigations", label: "Mitigations" , disabled: isMitigationTabDisabled },
                        { value: "comments", label: "COMMENTS" },
                        { value: "log", label: "LOG" }
                    ].map((t) => (
                        <Tab
                            key={t.value}
                            sx={{ height: "auto", minHeight: "auto" }}
                            label={t.label}
                            value={t.value}
                            disabled={t.disabled}
                        />
                    ))}
                </Tabs>
                <Divider />
                <Box flex={1} overflow="hidden">
                    <TabPanel value="overview" sx={{ p: 0, height: "100%" }}>
                        <RiskDetailsOverview
                            riskId={riskId}
                            onEdit={onEdit}
                            refetch={refetch}
                        />
                    </TabPanel>
                    <TabPanel value="causes" sx={{ p: 2, height: "100%" }}>
                        <RaiseCause
                            isCauseFetching={isCauseFetching}
                            isCausePending={isCausePending}
                            setCausesData={setCausesData}
                            onClearCause={onClearCause}
                            isSaving={causeSaving}
                            onSaveCause={onSaveCause}
                            editCauseStatus={editCauseStatus}
                            causeData={causeData}
                            causesData={causesData}
                            onEditCause={onEditCause}
                        />
                    </TabPanel>
                    <TabPanel value="assessment" sx={{ p: 2, height: "100%" }}>
                        <RaiseAssessment
                            isAssessmentFetching={isAssessmentFetching}
                            isAssessmentPending={isAssessmentPending}
                            isSaving={assessmentSaving}
                            assessmentData={assessmentData}
                            impactData={impactData}
                            setImpactData={setImpactData} onSaveImpact={onSaveImpact} onClearImpact={onClearImpact}
                            editImpactStatus={editImpactStatus}
                            onEditImpact={onEditImpact}
                        />
                    </TabPanel>
                    <TabPanel value="mitigations" sx={{ p: 2, height: "100%" }}>
                        <RaiseMitigation
                            isMitigationFetching={isMitigationFetching}
                            isMitigationPending={isMitigationPending}
                            setMitigationsData={setMitigationsData}
                            onClearMitigation={onClearMitigation}
                            isSaving={mitigationSaving}
                            onSaveMitigation={onSaveMitigation}
                            editMitigationStatus={editMitigationStatus}
                            mitigationData={mitigationData}
                            mitigationsData={mitigationsData}
                            onEditMitigation={onEditMitigation}
                        />
                    </TabPanel>
                    <TabPanel value="comments" sx={{ p: 2, height: "100%" }}>
                        <Comments
                            topic={['PROJECT_RISK']}
                            sub_event={['RISK_COMMENT_ADDED']}
                            object_id={String(riskId)}
                            parent_id={undefined}
                        />
                    </TabPanel>
                    <TabPanel value="log" sx={{ pl: 2, py: 2, pr: 0, height: "100%" }}>
                        <Log
                            topic={['PROJECT_RISK']}
                            sub_event={[]}
                            object_id={String(riskId)}
                            parent_id={undefined}
                        />
                    </TabPanel>
                </Box>
            </TabContext>
        </Box>
    );
};